import { exhaustiveCheck, invariant } from "@banjoanton/utils";
import type { ChessColor } from "../models/base-model";
import type {
    ChessGame,
    ChessPlayer,
    ChessSimpleResult,
} from "../models/chess-game-model";
import type { ChessComResult } from "../features/import/models/chess-com-models";

const getPlayerResultFromTag = (
    playerIsWhite: boolean,
    tags: Record<string, string>
): ChessSimpleResult => {
    const result = tags.Result;
    invariant(result, "Result tag is missing in the game");
    if (result === "1/2-1/2") {
        return "draw";
    }

    if (playerIsWhite) {
        if (result === "1-0") {
            return "win";
        }
        return "loss";
    }

    if (result === "0-1") {
        return "win";
    }
    return "loss";
};

const getPlayerResultFromResult = (
    result: ChessComResult
): ChessSimpleResult => {
    const winResults: ChessComResult[] = ["win"];
    const lossResults: ChessComResult[] = [
        "checkmated",
        "timeout",
        "resigned",
        "lose",
        "bughousepartnerlose",
    ];
    const drawResults: ChessComResult[] = [
        "agreed",
        "repetition",
        "stalemate",
        "insufficient",
        "50move",
        "abandoned",
        "kingofthehill",
        "threecheck",
        "timevsinsufficient",
    ];

    if (winResults.includes(result)) {
        return "win";
    }

    if (lossResults.includes(result)) {
        return "loss";
    }

    if (drawResults.includes(result)) {
        return "draw";
    }

    throw new Error(`Unknown result: ${result}`);
};
export type PlayerWithColor = ChessPlayer & { color: ChessColor };
export type PlayersWithColors = {
    user: PlayerWithColor;
    opponent: PlayerWithColor;
};

const getPlayers = (game: ChessGame): PlayersWithColors => {
    const opponentPlayerModel =
        game.playerColor === "w" ? game.black : game.white;
    const opponentColor: ChessColor = game.playerColor === "w" ? "b" : "w";

    const opponent = { ...opponentPlayerModel, color: opponentColor };
    const user = { ...game.player, color: game.playerColor };

    return { user, opponent };
};

const normalizeFen = (fen: string): string => {
    const [pieces, side, castling] = fen.split(" ");
    return [pieces, side, castling || "-", "-"].join(" ");
};

export const GameUtil = {
    getPlayers,
    normalizeFen,
    getPlayerResultFromTag,
    getPlayerResultFromResult,
};
